import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { resolveApiMessage, showToast } from '../../../helpers';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import { getOpsClassesMeta } from '../../../requests';
import {
  createOpsClassSeries,
  downloadOpsClassIcs,
  getOpsClassProgramNotes,
  getOpsClassSeries,
  getZoomRoomOptions,
} from '../../../requests/opsRecords';
import {
  createOpsClassEx,
  getOpsClassEx,
  getOpsClassRelatedEx,
  TOpsClassUpsertEx,
  updateOpsClassEx,
} from '../../../requests/opsRegistrationsExtra';
import { getReservationStage } from '../../../requests/opsScheduling';
import { TOpsClassUpsert } from '../../../types';
import AttachmentsPanel from '../AttachmentsPanel';
import CommentThread from '../CommentThread';
import { btnPrimary, btnSecondary, PageHeader, Pill } from '../ui';
import ContactsSection from './ContactsSection';
import CsmSection from './CsmSection';
import DatesSection from './DatesSection';
import DeliverySection from './DeliverySection';
import DetailsSection from './DetailsSection';
import LinksSection from './LinksSection';
import LocationSection from './LocationSection';
import OfferTimesCard from './OfferTimesCard';
import PaymentSection from './PaymentSection';
import ProgramNoteCard from './ProgramNoteCard';
import {
  EvalResponsesCard,
  InstructorEvalCard,
  RegistrationsCard,
  RelatedCasesCard,
} from './RelatedCards';
import ReservationStageCard from './ReservationStageCard';
import SendSection from './SendSection';
import SeriesCard from './SeriesCard';
import { EMPTY_FORM, formFromClass, formToDto, toLocalInput } from './shared';
import SmeAssignmentsCard from './SmeAssignmentsCard';
import TrainerSection from './TrainerSection';
import ZoomRoomsCard from './ZoomRoomsCard';

// The panels own their edit state; the data queries stay here so they all
// start together on page load rather than waiting for the class to arrive.
const OpsClassEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefillDate = searchParams.get('date');

  const [form, setForm] = useState(EMPTY_FORM);
  // New class only: extra sessions make this a series (session 1 = the form)
  const [extraDates, setExtraDates] = useState<
    { start: string; end: string }[]
  >([]);
  useEffect(() => {
    if (isNew && prefillDate) {
      setForm((f) => ({
        ...f,
        start: `${prefillDate}T09:00`,
        end: `${prefillDate}T17:00`,
      }));
    }
  }, []);

  const [accountId, setAccountId] = useState('');
  const [accountName, setAccountName] = useState('');

  const { data: meta } = useQuery({
    queryKey: ['opsClassesMeta'],
    queryFn: getOpsClassesMeta,
  });

  const { data: existing, isLoading } = useQuery({
    queryKey: ['opsClass', id],
    queryFn: () => getOpsClassEx(id!),
    enabled: !isNew,
  });

  const { data: related } = useQuery({
    queryKey: ['opsClassRelated', id],
    queryFn: () => getOpsClassRelatedEx(id!),
    enabled: !isNew,
  });

  useEffect(() => {
    if (!existing) return;
    setForm(formFromClass(existing));
    setAccountId(existing.accountId ?? '');
    setAccountName(existing.accountName ?? '');
  }, [existing]);

  // The notes cascade: the program this class belongs to, and its note
  const { data: programNotes } = useQuery({
    queryKey: ['opsClassProgramNotes', id],
    queryFn: () => getOpsClassProgramNotes(id!),
    enabled: !isNew,
  });

  // Series siblings (portal-born, or read through the Salesforce Opportunity)
  const { data: series } = useQuery({
    queryKey: ['opsClassSeries', id],
    queryFn: () => getOpsClassSeries(id!),
    enabled: !isNew,
  });

  // Reservation stage — the portal's replacement for the GoMeddo status ladder
  const { data: stage, refetch: refetchStage } = useQuery({
    queryKey: ['opsReservationStage', id],
    queryFn: () => getReservationStage(id!),
    enabled: !isNew,
  });
  const { data: roomOptions, refetch: refetchRooms } = useQuery({
    queryKey: ['opsZoomOptions', id],
    queryFn: () => getZoomRoomOptions(id!),
    enabled: !isNew && Boolean(existing?.start && existing?.end),
  });

  const [icsBusy, setIcsBusy] = useState(false);
  const downloadIcs = async () => {
    if (!id) return;
    setIcsBusy(true);
    try {
      const blob = await downloadOpsClassIcs(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(form.name || 'class').replace(/[^\w.-]+/g, '-')}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      showToast(resolveApiMessage(err as never), 'error');
    } finally {
      setIcsBusy(false);
    }
  };

  const createSeries = useMutation({
    mutationFn: (args: {
      template: TOpsClassUpsert;
      dates: { start: string; end: string }[];
    }) => createOpsClassSeries(args),
    onSuccess: (out) => {
      showToast(`Series created · ${out.count} sessions`, 'success');
      navigate(PATH.opsClasses);
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  const save = useMutation({
    mutationFn: (dto: TOpsClassUpsertEx) =>
      isNew ? createOpsClassEx(dto) : updateOpsClassEx(id!, dto),
    onSuccess: (saved) => {
      const cascade = (
        saved as {
          cascade?: {
            cancelledRegistrations?: number;
            notified?: { queued: number; suppressed: number; noEmail: number };
            instructor?: {
              kind: 'cancelled' | 'rescheduled';
              instructor: string | null;
              outcome: string;
            };
          };
        }
      ).cascade;
      const n = cascade?.cancelledRegistrations;
      let message = isNew
        ? 'Class created'
        : n
        ? `Class cancelled · ${n} open ${
            n === 1 ? 'seat' : 'seats'
          } cancelled with it`
        : 'Class saved';
      if (!isNew && n && cascade?.notified) {
        const { queued, suppressed } = cascade.notified;
        if (queued) message += ` · ${queued} emailed`;
        if (suppressed) message += ` · ${suppressed} held (quiet account)`;
      }
      // The trainer's notice on a move or cancel — what happened to it
      const tn = cascade?.instructor;
      if (!isNew && tn && tn.instructor) {
        const verb = tn.kind === 'cancelled' ? 'cancellation' : 'reschedule';
        if (tn.outcome === 'no-email') {
          message += ` · ${tn.instructor} has no email on file, ${verb} notice not sent`;
        } else if (tn.outcome === 'no-template') {
          message += ` · instructor ${verb} template missing, notice not sent`;
        } else if (tn.outcome === 'failed') {
          message += ` · ${verb} notice to ${tn.instructor} failed`;
        } else {
          message += ` · ${verb} notice ${
            tn.outcome === 'held' ? 'queued for' : 'sent to'
          } ${tn.instructor}`;
        }
      }
      showToast(message, 'success');
      navigate(PATH.opsClasses);
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  // Only an existing class has seats to release and learners to tell
  const cancelling = !isNew && form.status === 'Cancelled';

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.start || !form.end) {
      showToast('Name, start and end are required', 'error');
      return;
    }
    const dto = formToDto(form, accountId, cancelling);
    if (isNew && extraDates.length) {
      if (extraDates.some((d) => !d.start || !d.end)) {
        showToast('Every session needs a start and an end', 'error');
        return;
      }
      createSeries.mutate({
        template: dto,
        dates: [
          { start: dto.start, end: dto.end },
          ...extraDates.map((d) => ({
            start: new Date(d.start).toISOString(),
            end: new Date(d.end).toISOString(),
          })),
        ],
      });
      return;
    }
    save.mutate(dto);
  };

  const addSession = () => {
    // Default the next session to a week after the last one, same times
    const last = extraDates[extraDates.length - 1] ?? {
      start: form.start,
      end: form.end,
    };
    const shift = (v: string) => {
      if (!v) return '';
      const d = new Date(v);
      d.setDate(d.getDate() + 7);
      return toLocalInput(d.toISOString());
    };
    setExtraDates((x) => [
      ...x,
      { start: shift(last.start), end: shift(last.end) },
    ]);
  };

  const set = (key: keyof typeof form) => (value: string | boolean) =>
    setForm((f) => ({ ...f, [key]: value }));

  if (!isNew && isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={isNew ? 'New class' : form.name || 'Edit class'}
        back={{ to: PATH.opsClasses, label: 'Classes' }}
        meta={
          !isNew && existing ? (
            <span className="flex items-center gap-2">
              <Pill value={form.status} />
              {existing.classNumber && (
                <span>Class {existing.classNumber}</span>
              )}
            </span>
          ) : undefined
        }
        actions={
          !isNew ? (
            <>
              <button
                type="button"
                className={btnSecondary}
                disabled={icsBusy}
                onClick={downloadIcs}
                title="Download a calendar (.ics) file for this class"
              >
                {icsBusy ? 'Preparing…' : 'Calendar file'}
              </button>
              <Link
                className={btnSecondary}
                to={`${PATH.opsRegistrations}?classId=${id}`}
              >
                View roster
              </Link>
            </>
          ) : undefined
        }
      />
      <form onSubmit={submit} className="max-w-3xl px-8 pb-16">
        {!isNew && existing && !existing.localOrigin && (
          <div className="mb-4 rounded-lg border border-yellow bg-yellow-500 px-4 py-2.5 text-sm text-yellow-900">
            This class was born in Salesforce. Until cutover, a data refresh can
            overwrite these fields with Salesforce's version — make the change
            there too, or expect it to revert.
          </div>
        )}
        <div className="grid gap-5">
          {!isNew && series && series.items.length > 1 && (
            <SeriesCard series={series} />
          )}
          {!isNew && programNotes && (
            <ProgramNoteCard programNotes={programNotes} />
          )}

          {/* Section names and order mirror the Salesforce layout staff use
              today (docs/familiarity-spec.md) — familiar hallways, less furniture */}
          <DetailsSection
            form={form}
            set={set}
            isNew={isNew}
            cancelling={cancelling}
            existing={existing}
            meta={meta}
          />
          <DatesSection
            form={form}
            set={set}
            isNew={isNew}
            extraDates={extraDates}
            setExtraDates={setExtraDates}
            addSession={addSession}
          />
          <TrainerSection form={form} set={set} meta={meta} />
          <ContactsSection
            form={form}
            set={set}
            isNew={isNew}
            existing={existing}
            accountId={accountId}
            accountName={accountName}
            setAccountId={setAccountId}
            setAccountName={setAccountName}
          />
          <PaymentSection form={form} set={set} existing={existing} />
          <CsmSection form={form} set={set} />
          <LocationSection form={form} set={set} />
          <DeliverySection
            form={form}
            set={set}
            id={id}
            isNew={isNew}
            existing={existing}
          />
          <LinksSection
            form={form}
            set={set}
            isNew={isNew}
            existing={existing}
          />
          {!isNew && id && existing && (
            <SendSection id={id} existing={existing} />
          )}
        </div>

        <div className="sticky bottom-0 z-10 mt-5 flex items-center gap-3 rounded-xl border border-white-regular bg-white px-5 py-3 shadow-upper">
          <button
            type="submit"
            disabled={save.isPending || createSeries.isPending}
            className={btnPrimary}
          >
            {save.isPending || createSeries.isPending
              ? 'Saving…'
              : isNew
              ? extraDates.length
                ? `Create ${extraDates.length + 1} sessions`
                : 'Create class'
              : 'Save changes'}
          </button>
          <button
            type="button"
            className={btnSecondary}
            onClick={() => navigate(PATH.opsClasses)}
          >
            Cancel
          </button>
          {existing?.classNumber && (
            <span className="ml-auto text-sm text-gray-bold">
              Class {existing.classNumber}
            </span>
          )}
        </div>
      </form>

      {!isNew && id && (
        <div className="max-w-3xl px-8 pb-6">
          <CommentThread recordId={id} recordObject="class" />
          <AttachmentsPanel recordId={id!} recordObject="sf_event_request__c" />
        </div>
      )}

      {!isNew && id && (
        <div className="max-w-3xl px-8 pb-6">
          <OfferTimesCard
            classId={id}
            className={form.name}
            timeZone={form.timeZone}
          />
        </div>
      )}

      {!isNew && related && (
        <div className="max-w-5xl px-8 pb-24">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <SmeAssignmentsCard id={id!} related={related} />
            <RegistrationsCard id={id!} related={related} existing={existing} />
            <EvalResponsesCard related={related} />
            <InstructorEvalCard related={related} />
            <RelatedCasesCard id={id!} related={related} existing={existing} />
            {!isNew && stage && (
              <ReservationStageCard
                id={id!}
                stage={stage}
                refetchStage={refetchStage}
              />
            )}
            <ZoomRoomsCard
              id={id!}
              related={related}
              roomOptions={roomOptions}
              refetchRooms={refetchRooms}
            />
          </div>
        </div>
      )}
    </>
  );
};

// A fresh editor per class: jumping to another session of a series must not
// carry a half-finished edit (a stage pick, a sign-up sheet draft, an
// account search) onto the next class.
const OpsClassEditorForClass: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <OpsClassEditor key={id ?? 'new'} />;
};

export default OpsClassEditorForClass;
